import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import Grid from "@mui/material/Grid";
import featureZoneAnimation from "../../../img/featureZoneAnimation.gif";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { Warehouse } from "../../models/warehouse/types";
import { setOpenRequestedAt } from "../../store/slices/warehouseSlice";
import StaticMapComponent from "../../components/shared/staticMapComponent";
import BuilderIOSection from "../../builderio/components/builderIOSection";
import SafeHtml from "../../builderio/custom-components/SafeHtml/SafeHtml";
import { BuilderIOSections } from "../../models/constants";
import CartToProductListSidebar from "../cart/cartToProductListSidebar";
import ProductListEnumType from "@/enums/productListEnumType";
import { useState } from "react";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import { CartItemRequest } from "@/app/models/product/types";
import dataService from "@/app/services/dataService";
import React from "react";
import ItemSourceEnum from "@/enums/itemSourceEnum";

const HomepageFeatureZone: React.FC = () => {
  const {
    hoursFrom: warehouseHoursFrom,
    hoursTo: warehouseHoursTo,
    branchMessage: branchMsg,
    globalMessage: message,
    warehouses: allWarehouses,
  } = useSelector((state: RootState) => state.warehouseState.warehouseInfo);

  const {
    warehouseCode,
    warehouseName,
    warehouseAddress,
    warehouseCity,
    warehouseState,
    zipCode,
  } = useSelector((state: RootState) => state.warehouseState.selectedWarehouse);

  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { setNotification } = useGlobalNotification();
  const [loading, setLoading] = useState(false);

  let branchClosedMessage: string = "Branch Currently Closed.";
  var branchOpenMessage: string = "OPEN NOW!";
  let messageArray: string[] = [];
  if (branchMsg != null && branchMsg.includes(branchClosedMessage))
    messageArray = [
      branchClosedMessage,
      branchMsg.slice(branchClosedMessage.length),
    ];
  else if (branchMsg !== null && branchMsg.includes(branchOpenMessage))
    messageArray = [
      branchOpenMessage,
      branchMsg.slice(branchOpenMessage.length),
    ];
  else if (message !== null) messageArray = [message];

  let filteredWarehouses: Warehouse[] = allWarehouses.filter(
    (x) => x.warehouseCode === warehouseCode
  );

  const onUseCurrentLocation = () => {
    dispatch(setOpenRequestedAt(new Date()));
  };
  const isAuthenticated = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );
  const hasMostPurchased = useSelector(
    (state: RootState) => state.userState.user.hasMostPurchased
  );
  const shopProductsVisible = useSelector(
    (state: RootState) => state.userState.user.menu.shopProductsVisible
  );
  const hasDashboard = useSelector(
    (state: RootState) => state.userState.user.hasDashboard
  );
  const shareCartItems = useSelector(
    (state: RootState) => state.userState.user.shareCartItemDtos
  );

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);

  const addCartToNewTemplate = async (
    name?: string,
    description?: string,
    type?: ProductListEnumType
  ) => {
    if (!type || !name) {
      return;
    }

    try {
      setLoading(true);
      const response = await dataService.createNewEstimateOrTemplate(
        type,
        name.trim(),
        (description ?? "").trim()
      );
      if (
        response.success &&
        (response.data.errorMessage == null ||
          response.data.errorMessage?.trim() === "")
      ) {
        addCartToTemplate(response.data.id?.toString() ?? "0", type);
      } else if (
        response.data.errorMessage &&
        response.data.errorMessage.trim() !== ""
      ) {
        setNotification(response.data.errorMessage, "error");
      }
    } catch (error) {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    } finally {
      setLoading(false);
      setSidebarOpen(false);
    }
  };

  const addCartToTemplate = async (
    productListId: string,
    type?: ProductListEnumType
  ) => {
    try {
      setLoading(true);
      let response;
      if (type === ProductListEnumType.QuoteList) {
        response = await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Product,
          products: shareCartItems,
        });
      } else {
        response = await dataService.AddProductToTemplates({
          templateId: productListId,
          source: ItemSourceEnum.Product,
          products: shareCartItems,
        });
      }

      const { success, data } = response || {};
      const {
        warnings = [],
        id,
        productListName: name,
        newProductListId,
        newProductListName,
      } = data || {};

      if (success && warnings.length === 0) {
        const listTypeText =
          type === ProductListEnumType.QuoteList
            ? "Estimate List"
            : "Product Template";
        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        if (type !== ProductListEnumType.BranchList) {
          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={url}>{name}</a>.
            </>,
            "success"
          );
        } else {
          const newUrl = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${newProductListId}`;
          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={newUrl}>{newProductListName}</a>.{" "}
              <a href={url}>{name}</a> has been archived
            </>,
            "success"
          );
        }
      } else {
        setNotification(warnings[0] ?? "Failed to add product.", "error");
      }
    } catch (error) {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    } finally {
      setLoading(false);
      setSidebarOpen(false);
    }
  };

  return (
    <div className="homepageFeatureZoneContainer">
      <Grid
        container
        direction="row"
        spacing={2}
        sx={{ justifyContent: "center", alignItems: "stretch", p: 1 }}
      >
        <Grid item xs={12} lg={4} className="grid-1">
          <div className="mapHolder">
            <div className="branch">
              <div className="branchText">
                <h2 className="m-0">
                  Powered By Your Local <br />
                  <span className="branchTextNormal">{warehouseName}</span>
                  <br /> Branch
                </h2>
                <div style={{ lineHeight: "26px" }}>
                  {warehouseAddress}
                  <br />
                  {warehouseCity}, {warehouseState}, {zipCode}
                  <br />
                  <br />
                  <p>
                    {messageArray.map((x, index) => (
                      <span key={x}>
                        <SafeHtml as="span" html={x} />
                        {index !== messageArray.length - 1 && <br />}
                      </span>
                    ))}
                    <br />
                    <b>
                      {warehouseHoursFrom} - {warehouseHoursTo}
                    </b>
                  </p>
                  <Button
                    sx={{
                      color: "white",
                      p: 0,
                      mt: 2,
                      textAlign: "left",
                      textTransform: "capitalize",
                    }}
                    onClick={onUseCurrentLocation}
                  >
                    <MyLocationIcon fontSize="small" sx={{ mr: 1 }} /> Use My
                    Current Location
                  </Button>
                </div>
              </div>
            </div>
            {filteredWarehouses == null ? (
              <></>
            ) : (
              <StaticMapComponent
                selectedWarehouseCode={warehouseCode}
                warehouses={filteredWarehouses}
                longitudeCenterOffset={isMobile ? -0.26 : -0.2}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={12} lg={6} className="grid-2">
          {/* <Carousel
                        autoPlay={true}
                        interval={5000}
                        className="carouselContainer"
                        animation={"slide"}
                        duration={1000}
                        navButtonsAlwaysInvisible={true}
                        swipe={true}>
                        <div className="slide">
                            <h1>Supporting Your Business,<br />One Job at a Time</h1>
                            <h2>Seamlessly price and place orders, 24/7, with the dedicated support of your local Lansing team.</h2>
                            <Link className="whiteButton mt-1" to="/gallery?query=">Shop Now</Link>
                        </div>
                        <div className="slideBuilderWidget">
                            <BuilderIOSection section={BuilderIOSections.FeatureZoneSection} /> 
                        </div>
                    </Carousel> */}
          <BuilderIOSection
            section={BuilderIOSections.FeatureZoneSection}
            data={{ isShopNowVisible: !isAuthenticated || shopProductsVisible }}
          />
        </Grid>

        {shareCartItems && shareCartItems.length > 0 && (
          <>
            {loading && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  bgcolor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9000,
                  pointerEvents: "auto",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#1976d2" }} />
              </Box>
            )}
            <CartToProductListSidebar
              open={sidebarOpen}
              onCartToNewTemplate={(
                name?: string,
                description?: string,
                type?: ProductListEnumType
              ) => {
                if (name && type) {
                  addCartToNewTemplate(name, description, type);
                  setSidebarOpen(false);
                }
              }}
              onCartToTemplate={(id?: string, type?: ProductListEnumType) => {
                if (id && type) {
                  addCartToTemplate(id, type);
                }
              }}
              onClose={() => {
                setSidebarOpen(false);
              }}
            />
          </>
        )}

        {isAuthenticated ? (
          <Grid
            item
            xs={12}
            lg={2}
            className="grid-3 mb-5percLast"
            sx={{
              display: { xs: "flex", md: "block" },
              flexDirection: "rows",
              alignItems: "top",
              gap: "10px",
            }}
          >
            {hasDashboard ? (
              <div
                className="carouselContainerSmallLogged mb-5perc mobHeightBox"
                style={{
                  height: hasMostPurchased ? "42%" : "93.5%",
                  backgroundColor: "#40C1AC",
                }}
              >
                <Typography variant="h5" className="text-white">
                  <b>My Dashboard</b>
                </Typography>
                <Typography className="text-white">
                  {" "}
                  Schedule or review upcoming and recent deliveries.{" "}
                </Typography>
                <Link className="view-now-btn w" to="/dqdashboard">
                  View Now
                </Link>
              </div>
            ) : (
              ""
            )}
            {hasMostPurchased ? (
              <div
                className="carouselContainerSmallLogged mobHeightBox"
                style={{
                  height: hasDashboard ? "42%" : "93.5%",
                  backgroundColor: "#0057B8",
                }}
              >
                <Typography variant="h5" className="text-white">
                  <b>My Most Purchased</b>
                </Typography>
                <Typography className="text-white">
                  Quickly check pricing or re-order your preferred products.{" "}
                </Typography>
                <Link
                  className="view-now-btn"
                  to={
                    process.env.REACT_APP_WEB_URL +
                    "/ProductListItem/FrequentlyPurchasedTemplate"
                  }
                >
                  View Now
                </Link>
              </div>
            ) : (
              ""
            )}
          </Grid>
        ) : (
          <Grid item xs={12} lg={2} className="grid-3">
            <div className="carouselContainerSmall">
              <img
                className="animation"
                src={featureZoneAnimation}
                alt="Homepage Feature Zone Animation"
                width="auto"
                height="auto"
              />
              <div className="linkContainer">
                <Link
                  className="whiteButton my-1"
                  to={process.env.REACT_APP_WEB_URL + "/quicklogin"}
                >
                  Log In
                </Link>
                <Typography>
                  Not Registered?
                  <br />
                  <Link
                    style={{ color: "#353535" }}
                    to={process.env.REACT_APP_WEB_URL + "/register"}
                  >
                    Create Account
                  </Link>
                </Typography>
              </div>
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default HomepageFeatureZone;
